import React, { useMemo, useState } from "react";
import axios from "axios";
import { Download, FileJson, ReceiptText, ShieldCheck } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { useTenant } from "../../context/TenantContext";
import { MODULE_LABELS } from "../../utils/moduleAccess";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

const downloadJson = (filename: string, data: unknown) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const SettingsExport: React.FC = () => {
  const { tenant, moduleFlags } = useTenant();
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [message, setMessage] = useState<string>("");
  const token = localStorage.getItem("token") || "";

  const accessSnapshot = useMemo(
    () => ({
      exportedAt: new Date().toISOString(),
      user: {
        id: localStorage.getItem("userId"),
        code: localStorage.getItem("userCode"),
        name: localStorage.getItem("userName"),
        role: localStorage.getItem("role")
      },
      tenant: tenant
        ? {
            id: tenant.id,
            name: tenant.name,
            subdomain: tenant.subdomain,
            status: tenant.status,
            billing_email: tenant.billing_email
          }
        : null,
      enabledModules: Object.entries(moduleFlags)
        .filter(([, enabled]) => enabled)
        .map(([key]) => ({
          key,
          label: MODULE_LABELS[key as keyof typeof MODULE_LABELS] || key
        }))
    }),
    [moduleFlags, tenant]
  );

  const exportTenantProfile = async () => {
    try {
      setLoadingKey("tenant");
      setMessage("");
      const response = await axios.get(getApiUrl("tenant/current"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      downloadJson("school-profile-export.json", response.data);
      setMessage("School profile export downloaded.");
    } catch (error: any) {
      setMessage(error?.response?.data?.message || "Failed to export school profile.");
    } finally {
      setLoadingKey(null);
    }
  };

  const exportSubscription = async () => {
    try {
      setLoadingKey("subscription");
      setMessage("");
      const response = await axios.get(getApiUrl("tenant/subscription"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      downloadJson("subscription-export.json", response.data);
      setMessage("Subscription summary downloaded.");
    } catch (error: any) {
      setMessage(error?.response?.data?.message || "Failed to export subscription summary.");
    } finally {
      setLoadingKey(null);
    }
  };

  const exportAccessSnapshot = () => {
    setLoadingKey("access");
    setMessage("");
    downloadJson("access-snapshot.json", accessSnapshot);
    setMessage("Access snapshot downloaded.");
    setLoadingKey(null);
  };

  const cards = [
    {
      key: "tenant",
      title: "School Profile Export",
      description: "Download the current tenant profile, branding, and school-level deployment data.",
      action: exportTenantProfile,
      Icon: FileJson
    },
    {
      key: "subscription",
      title: "Subscription Export",
      description: "Download the active subscription or billing summary for review and renewal planning.",
      action: exportSubscription,
      Icon: ReceiptText
    },
    {
      key: "access",
      title: "Access Snapshot",
      description: "Export the current admin session summary, school context, and enabled module list.",
      action: exportAccessSnapshot,
      Icon: ShieldCheck
    }
  ];

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="utopia-page-shell">
          <section className="utopia-page-hero">
            <div>
              <p className="utopia-eyebrow">Export Center</p>
              <h1 className="utopia-page-title">Download school configuration snapshots</h1>
              <p className="utopia-page-subtitle">
                Export current tenant details, subscription information, and access configuration without leaving settings.
              </p>
            </div>
          </section>

          <section className="grid gap-5 lg:grid-cols-3">
            {cards.map(({ key, title, description, action, Icon }) => (
              <article key={key} className="utopia-card p-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
                    <Icon size={20} />
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">JSON Export</span>
                </div>
                <h2 className="mt-5 text-lg font-black text-slate-900">{title}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-500">{description}</p>
                <button
                  type="button"
                  onClick={action}
                  disabled={loadingKey === key}
                  className="utopia-btn-primary mt-6 inline-flex w-full items-center justify-center gap-2 py-3 disabled:opacity-60"
                >
                  <Download size={16} />
                  {loadingKey === key ? "Preparing export..." : "Download export"}
                </button>
              </article>
            ))}
          </section>

          <section className="utopia-card p-6">
            <h2 className="text-lg font-black text-slate-900">Export notes</h2>
            <ul className="mt-4 space-y-3 text-sm text-slate-600">
              <li>Exports are limited to the currently authenticated school context.</li>
              <li>Subscription exports are hidden by policy for exempt schools only through the billing UI, but the underlying summary remains auditable for admins.</li>
              <li>Use Financial Reports for transactional fee and payment exports.</li>
            </ul>
            {message && (
              <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
                {message}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default SettingsExport;
